import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { loadEndPointsData } from './helper';
import { epRefreshAtom } from '../state';

export const forceRerenderUIAtom = atom<string>('');

export const orderByAtom = atomWithReset<string>('vuln');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const orderingAtom = atom(async (get) => {
  const orderBy = get(orderByAtom);
  const order = get(sortOrderAtom);
  return `${order === 'desc' ? '-' : ''}${orderBy}`;
});

export const selectedEnpointsFilterAtom = atomWithReset<
  'active' | 'aging' | 'deleted'
>('active');
export const scannerTypeFilterAtom = atomWithReset<string>('');
export const searchPhraseFilterAtom = atomWithReset<string>('');

export const checkedListAtom = atomWithReset<number[]>([]);
export const isCheckAllAtom = atomWithReset(false);

export const DEFAULT_ACTIVE_EP_LISTING_COLUMNS = [
  'ip_with_type',
  'alias',
  'asset_name',
  'vuln',
  'ca_created',
  'scan__launch_datetime',
  'actions',
];
export const activeEPColumnKeysAtom = atomWithStorage(
  'ep_list_active_column_keys',
  DEFAULT_ACTIVE_EP_LISTING_COLUMNS,
);

export const DEFAULT_AGED_OUT_EP_LISTING_COLUMNS = [
  'ip_with_type',
  'alias',
  'asset_name',
  'deleted_endpoint__open_vulnerability_count',
  'deleted_endpoint__closed_vulnerability_count',
  'deleted_endpoint__deleted_on',
  'deleted_endpoint__deleted_by',
  'actions',
];
export const agedOutEPColumnKeysAtom = atomWithStorage(
  'ep_list_aged_out_column_keys',
  DEFAULT_AGED_OUT_EP_LISTING_COLUMNS,
);

export const paginationConfigAtom = atomWithReset({
  page_size: DEFAULT_PAGE_SIZE,
  page: 1,
});

// component height
export const componentHeightAtom = atom(0);

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsVulnMapAtom = atom<Record<number, number>>({});

export const vulnCountForCheckedHostsAtom = atom(async (get) => {
  const checkedHostIds = get(checkedListAtom);
  const vulnMap = get(checkedHostsVulnMapAtom);
  let total = 0;

  if (checkedHostIds?.length > 0) {
    const allHosts = get(epListingDataAtom);

    // Update vulnMap with any new hosts from current page
    const currentPageHosts = allHosts?.items?.filter((host) =>
      checkedHostIds.includes(host.id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored value if available, otherwise 0
      total += vulnMap[hostId] || 0;
    });

    // Update totals with any new values from current page
    currentPageHosts?.forEach((host) => {
      if (host.vuln !== vulnMap[host.id]) {
        total = total - (vulnMap[host.id] || 0) + (host.vuln || 0);
      }
    });
  }
  return total;
});

export const epListingDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const currentPage = paginationConfig.page;

  const ordering = get(orderingAtom);
  const scannerTypeFilter = get(scannerTypeFilterAtom);
  const selectedEndpointsFilter = get(selectedEnpointsFilterAtom);
  const searchPhraseFilter = get(searchPhraseFilterAtom);
  const refresh = get(epRefreshAtom);

  try {
    const responseData = await loadEndPointsData(
      companyId,
      pageSize,
      currentPage,
      ordering,
      scannerTypeFilter,
      searchPhraseFilter,
      selectedEndpointsFilter,
    );
    return {
      items: responseData.items,
      count: responseData.count,
    };
  } catch (error) {
    console.error(error);
  }

  return {
    items: undefined,
    count: 0,
  };
});

export const forceRefreshStatusAtom = atom<string>('');
export const endpointsStatusDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const forceRefresh = get(forceRefreshStatusAtom);
  const endpointData = get(epListingDataAtom);
  try {
    // only load on force refresh
    if (companyId && companyId !== -1 && endpointData?.items && forceRefresh) {
      const params = {
        company_id: companyId,
        endpoint_ids: endpointData?.items.map((i) => i.id).join(','),
      };
      const responseData = await callApi('endpoints/status', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});
